//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { find , get , has } from 'lodash';

//  -- Components ---------------------

//  -- Constants ----------------------
import COLOR from 'utils/constants/color';
import { Drawer, Tooltip } from 'antd';
import StudentSubmissionView from 'features/forms/StudentSubmissionView';
import { checkIsManualGradable, isBeforTime } from 'utils/methods/math';
import { NoteMark } from './UI';
import { getExceptionStatus, imageFetch, imageFetchFromPodcast, isVideo } from 'utils/lib/ebookHelper';
import { useAllBook } from 'store/books/selectors';
import { useResources } from 'store/resources/selectors';
import { usePageCover } from './hooks/pageCover/pageCover';
import { useCurrentUser } from 'store/users/selectors';
import { Check, Close } from '@mui/icons-material';
import completeModeSvg from "../utils/assets/images/completeMode.svg";
import pastDueSvg from "../utils/assets/images/pastDue.svg";
import pastDueIncompleteSvg from "../utils/assets/images/pastDueIncomplete.svg";
import lastSubSvg from "../utils/assets/images/last-sub.svg";
import clockSvg from "../utils/assets/images/Clock.svg";
import dueDateSvg from "../utils/assets/images/dueDate.svg";
import SentryWrap from 'components/Sentry';

//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------
const Column = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 4px;
`;

const Row = styled.div`
    align-items: ${p => p.align || 'center'};
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    margin-bottom: 16px;
    width: 100%;

    &:last-child {
        align-items: flex-end;
        justify-content: space-between;
        margin-bottom: 0px;
    }
`;

const Title = styled.span`
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    text-align: left;
`;

const INCOMPLETE_STATUS_COLOR = '#D48C45';

const completionStripeColors = {
  pastDue: { border: '#F00', bar: '#f00505' },
  exceptionDanger: { border: '#e74c3c', bar: '#e74c3c' },
  incomplete: { border: INCOMPLETE_STATUS_COLOR, bar: INCOMPLETE_STATUS_COLOR },
  excused: { border: '#27ae60', bar: '#27ae60' },
  success: { border: '#64DD16', bar: '#64DD16' },
};

const Wrapper = styled.div`
    border: 2px solid
      ${(p) => completionStripeColors[p.completionStripe]?.border ?? completionStripeColors.success.border};
    border-radius: 11px;
    color: ${COLOR.green100};
    font-size: 14px;
    font-weight: 400;
    margin-top: 16px;
    outline: none;
    padding: 12px 12px 10px;
    transition: all 300ms ease;
    width: 100%;
    background-color: ${COLOR.white};
    position: relative;
    padding-left: 30px !important;

    &::before {
        width: 20px;
        height: 100%;
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        background: ${(p) => completionStripeColors[p.completionStripe]?.bar ?? completionStripeColors.success.bar};
        content: "";
        border-radius: 8px 0px 0px 8px;
    }
`;

const GradeBadge = ({ grade }) => {
    if (!grade) return null;

    const gradeConfig = {
        Missing: { color: '#e74c3c', label: 'Missing' },
        Incomplete: { color: INCOMPLETE_STATUS_COLOR, label: 'Incomplete' },
        Excused: { color: '#27ae60', label: 'Excused' },
    };

    const config = gradeConfig[grade] || { color: '#333', label: grade };

    return (
        <span style={{ color: config.color }}>
            {config.label}
        </span>
    );
};

const PastAssignment = ({
    assignment,
    submissions,
    is_extension_activity = false,
    is_engage_activity = false
}) => {
    const bookAllList = useAllBook();
    const resources = useResources();
    const activeSubmission = find(submissions, ['assignmentId', assignment.data.id]);
    const bookDescription = get(assignment,"data.bookDescription")
    const displayname  = assignment?.displayName || get(assignment,"data.displayName","")
    const ungraded = get(assignment, "ungraded", false)
    const grade_type = get(assignment,"grade_type");
    const chapter = get(assignment, "data.chapter")
    const assignment_page =  get(assignment, "data.page")
    const isCompleteInComplete = grade_type === "complete_incomplete"
    const max_point = assignment?.max_point && !isNaN(assignment?.max_point) ? Number.parseInt(assignment.max_point, 10) : 100
    const view_result = useMemo(() => {
        const result_visibility_on = assignment?.result_visibility_on;
        return result_visibility_on ? isBeforTime(result_visibility_on) : true
    }, [])
    const currentUser = useCurrentUser();
    const { isException, exceptionStatusText } = getExceptionStatus(activeSubmission,currentUser);
    const isPointSystem =  grade_type === "points"
    const [reviewDrawer,setEeviewDrawer] = useState(false)
    const pageCover = usePageCover({ assignmentData: assignment?.data });

    const modalSrc = useMemo(() => pageCover ? pageCover : isVideo(chapter, resources) ? imageFetchFromPodcast(chapter, resources) : imageFetch(bookAllList, chapter, assignment_page), [resources, bookAllList, pageCover]);
    const getCompleteInclompleteMark = (isComplete)=>{
        if(isComplete){
          return <Check style={{ color: COLOR.green100, fontSize: 18 }} />
        } else{
          return <Close style={{ color: COLOR.red, fontSize: 18 }} />
        }
    }
    const getGrade = () => {
        if(isException) return exceptionStatusText;
        const grade = activeSubmission?.grade >= 0 ? activeSubmission?.grade : '';
        const in_progress = activeSubmission && !activeSubmission.complete && activeSubmission.retaking
        const bg_text = activeSubmission&&activeSubmission.ai_grade&&activeSubmission.ai_grade.length > 0?"TBG - AI": "TBG";
        // const bg_text = "TBG - AI";
        if (in_progress) return "in progress";
        let isTbgObj = checkIsManualGradable(get(activeSubmission, "assignment.questions"));
        // show tbg if its true
        const isTbg = activeSubmission?.manualGrading && isTbgObj
        const face_grade = activeSubmission?.face_grade;
        if(activeSubmission?.assignmentType === "bingo" && ungraded){
          const isSubmitted = activeSubmission?.answers?.length > 0 ? activeSubmission?.answers?.some((el)=> el?.is_submitted):false;
          return isSubmitted ? 'Credit' : '-';
        }
        if (ungraded) return 'Credit'
        if (isCompleteInComplete && has(activeSubmission, "id")) {
          return getCompleteInclompleteMark(activeSubmission?.grade > 0);
        } else if (isPointSystem) {
          if (isTbg) {
            return  face_grade > 0 ||  face_grade == 0 ? `${face_grade}/${max_point} (${bg_text})`:`${bg_text}/${max_point}`;
          }
          return face_grade > 0 ||  face_grade == 0
            ? `${face_grade}/${max_point}`
            : `0/${max_point}`;
        } else {
          if (isTbg) {
            if (grade>0||grade==0) return `${grade} (${bg_text})`
            return bg_text;
          }
          return grade == 0 || grade > 0 ? grade : '-';
        }
    }

    const oncloseDrawer = () => {
      setEeviewDrawer(false)
    }

    const onReviewClick = (e) => {
      e.preventDefault();
      e.stopPropagation()
      setEeviewDrawer(true)
    }

    const exceptionStatusNum = Number(activeSubmission?.exceptionStatus ?? 0);
    const isMissingCompleted =
      Boolean(activeSubmission?.complete && exceptionStatusNum === 3);
    const isIncompleteException =
      Boolean(activeSubmission?.complete && exceptionStatusNum === 2);
    const isExcusedException =
      Boolean(activeSubmission?.complete && exceptionStatusNum === 1);

    const completionStripe = !activeSubmission?.complete
      ? 'pastDue'
      : isMissingCompleted
        ? 'exceptionDanger'
        : isIncompleteException
          ? 'incomplete'
          : isExcusedException
            ? 'excused'
            : 'success';

    const completedRibbonLabel =
      isMissingCompleted
        ? 'Missing'
        : isIncompleteException
          ? 'Incomplete'
          : isExcusedException && exceptionStatusText
            ? exceptionStatusText
            : 'Completed';

    return (
    <SentryWrap>
        <Wrapper completionStripe={completionStripe}>
            <Drawer
                height='100%'
                destroyOnHidden
                onClose={() => {
                    oncloseDrawer()
                }}
                placement='top'
                open={reviewDrawer}
                key='assignmentNew'
                className='student-review-new'
            >
                <StudentSubmissionView
                    data={assignment}
                    submission={activeSubmission}
                    chapter={assignment?.data?.chapter || assignment.chapter}

                />
            </Drawer>
            <Row className='setFlexcenter'>
                {
                activeSubmission?.complete 
                    ? 
                    <img src={isMissingCompleted ? pastDueSvg : isIncompleteException ? pastDueIncompleteSvg : completeModeSvg} className='pastDueIcon'  width="" height={""} alt="" />  

                    : 
                    assignment?.data?.typeId === 'activity'
                    ? 
                    <img src={pastDueSvg} className='pastDueIcon' width="" height={""} alt="" />
                    :
                    // <Assignment style={{ height: '18px', marginRight: '4px' }} />
                    <img src={pastDueSvg} className='pastDueIcon'  width="" height={""} alt="" />
                }
                <Tooltip placement='top' title={displayname}>
                    <Title className={`text-wrap ${
                      !activeSubmission?.complete || isMissingCompleted || isIncompleteException
                        ? isIncompleteException
                          ? 'pastDueIncompleteTitle 111'
                          : 'pastDueTitle 111'
                        : 'completeTitleBlack'
                    }`}>{displayname}</Title>
                </Tooltip>             
            </Row>

            <div className='assignNotifyUpr assignNotifyUprStudentSide'>
             <div className='assignNotifyLft'>
                <Row className='studentCardInfo 222'>
                    <Column className="cardStudentSide">
                      {is_extension_activity && (
                        <span style={{ color: "#03989e" }}>
                            (Extension Activity)
                        </span>
                        )}     
                        {bookDescription ?
                        <div className="cardDetail">
                            <div className='cardDetailInr'>
                            <img src={lastSubSvg} alt="" title="" />
                            <p>Book:</p>
                            </div>  
                            <div className='setViewGrade'>
                            <span>{activeSubmission?.submissions || '0'}</span>
                                {/* {activeSubmission?.submissions > 0 && view_result ? (<span onClick={onReviewClick} className='viewGridCstm'>View Grade</span>) : null}  */}
                            </div>
                        </div>
                        : null }

                        <div className="cardDetail">
                        <div className='cardDetailInr'>
                        <img src={lastSubSvg} alt="" title="" />
                        <p>Submissions:</p>
                        </div>  
                         <div className='setViewGrade'>
                            <span>{activeSubmission?.submissions || '0'}</span>
                            {/* {activeSubmission?.submissions > 0 && view_result ? (<span onClick={onReviewClick} className='viewGridCstm'>View Grade</span>) : null}  */}
                         </div>
                        </div>

                        {get(assignment,"scheduleAt")?(
                        <div className="cardDetail">
                        <div className='cardDetailInr'>
                            <img src={clockSvg} alt="" title="" />
                            <p>Start Date:</p>
                        </div>  
                        <span>{moment(get(assignment,"scheduleAt")).format('MMM DD, YYYY hh:mm A')}</span>
                        </div> 
                        ):null}

                        <div className="cardDetail">
                        <div className='cardDetailInr'>
                        <img src={dueDateSvg} alt="" title="" />
                        <p>Due Date:</p>
                        </div>  
                        <span>{moment(get(assignment,"dueDate")).format('MMM DD, YYYY hh:mm A')}</span>
                        </div> 

                        {(activeSubmission?.graded|| !activeSubmission?.manualGrading)&& view_result && (
                            <div className="cardDetail">
                            <div className='cardDetailInr'>
                                <img src={lastSubSvg} alt="" title="" />
                                <p>Grade:</p>
                            </div> 
                            <GradeBadge grade={getGrade()} />
                            </div> 
                        )}
                           {is_engage_activity && (
                            <span style={{ color: "#03989e" }}>
                                (Engage Activity)
                            </span>
                        )}

                        {!view_result?<NoteMark note={"Grade and feedback will be show upon the teacher approval"} />:null}

                    </Column>
                </Row>
             </div>  
             <div className='assignNotifyRght assignNotifyRghtStudentSide'><img src={modalSrc}  width="" height={""} alt="" /></div>
            </div>  

            <div className='assignBtnStatus assignBtnsCollect'>
               {activeSubmission?.complete ?
                <button className={`assignCompleted ${
                  isMissingCompleted ? 'assignMissing' : ''
                } ${isIncompleteException ? 'assignIncomplete' : ''} ${isExcusedException ? 'assignExcused' : ''}`}>{completedRibbonLabel}</button>
                :
                <button className='assignPast'>Past Due</button>
               }
               <div className='viewGradeBtn'>
                   {activeSubmission?.submissions > 0 && view_result ? (<span onClick={onReviewClick}>View grade</span>) : null} 
                </div>
            </div>

            {/* Add status at Left */}

            <div className='addStatus'>
               {!activeSubmission?.complete ? (
                <label>Past Due</label>
               ) : (
                <label>{completedRibbonLabel}</label>
               )}
            </div>
            
        </Wrapper>
    </SentryWrap>
    );
};


export default PastAssignment;
